import type { EntregaRemisionFormState } from '../domain/EntregaRemisionFormState.ts';
import type { EntregaRemisionEditRecord, SaveEntregaRemisionInput } from '../domain/models/EntregaCatalog.ts';
import { validateEntregaRemisionFormErrors, clampEntregaRemisionSolicitadas } from '../domain/entregaRemisionValidation.ts';
import { firstFieldError } from '@src/modules/kernel/FieldErrors.ts';
import type { FormUseCaseResult } from '@src/modules/kernel/FormUseCaseResult.ts';
import { shortPublicId, nowDatetimeLocalValue } from '@src/modules/kernel/formDisplayHelpers.ts';
import type { EntregaApplicationService } from './EntregaApplicationService.ts';
import { assertEntregaMediaStored, persistVerifiedEntregaBlob, resolveEntregaMediaId } from './entregaMediaPersistence.ts';
import {
  blobHasSignatureInk,
  signatureInkAcceptableForSave,
  signatureInkAssertError,
} from '@src/modules/platform/signature/application/signatureBlobInk.ts';

/** Remisión `fechaHora` calendar day = entrega `fecha` (YYYY-MM-DD). Keeps time from `timeSource`. */
export function remisionFechaHoraFromEntregaFecha(
  entregaFecha: string,
  timeSource = nowDatetimeLocalValue(),
): string {
  const day = entregaFecha.trim().slice(0, 10);
  const time = timeSource.includes('T') ? timeSource.slice(11, 16) : timeSource.trim().slice(0, 5);
  return `${day}T${time || '00:00'}`;
}

export function createEmptyEntregaRemisionForm(entregaFecha?: string): EntregaRemisionFormState {
  return {
    nombreDespacha: '',
    vehiculo: '',
    fechaHora: entregaFecha?.trim()
      ? remisionFechaHoraFromEntregaFecha(entregaFecha)
      : nowDatetimeLocalValue(),
    cantidadSolicitadas: 0,
    cantidadEntregadas: 0,
    cantidadBolsasSolicitadas: 0,
    cantidadBolsasEntregadas: 0,
    observaciones: '',
    nombreRecibe: '',
    cedulaRecibe: '',
    firmaRecibeBlob: null,
    nombreEntrega: '',
    cedulaEntrega: '',
    firmaEntregaBlob: null,
  };
}

async function fechaHoraAlignedToEntrega(
  service: EntregaApplicationService,
  entregaPublicId: string,
  timeSource?: string,
): Promise<string | null> {
  const entrega = await service.entregas.getForEdit(entregaPublicId);
  const fecha = entrega?.fecha?.trim();
  if (!fecha) return null;
  return remisionFechaHoraFromEntregaFecha(fecha, timeSource);
}

/** Persist remisión fecha day = entrega fecha. No-op if already aligned / no remisiones. */
export async function alignEntregaRemisionesFechaHora(
  service: EntregaApplicationService,
  entregaPublicId: string,
  entregaFecha?: string,
): Promise<void> {
  const fecha =
    entregaFecha?.trim().slice(0, 10) ||
    (await service.entregas.getForEdit(entregaPublicId))?.fecha?.trim().slice(0, 10) ||
    '';
  if (!fecha) return;
  const rows = await service.children.listRemisionesByEntrega(entregaPublicId);
  let changed = false;
  for (const row of rows) {
    const edit = await service.children.getRemisionForEdit(row.publicId);
    if (!edit) continue;
    const fechaHora = remisionFechaHoraFromEntregaFecha(fecha, edit.fechaHora);
    if (fechaHora === edit.fechaHora.trim()) continue;
    const { publicId: _id, ...input } = edit;
    await service.children.updateRemision(edit.publicId, { ...input, fechaHora });
    changed = true;
  }
  if (changed) await service.flushLocalChanges();
}

export function remisionFormFromEdit(record: EntregaRemisionEditRecord): EntregaRemisionFormState {
  return {
    nombreDespacha: record.nombreDespacha,
    vehiculo: record.vehiculo,
    fechaHora: record.fechaHora,
    cantidadSolicitadas: record.cantidadSolicitadas,
    cantidadEntregadas: record.cantidadEntregadas,
    cantidadBolsasSolicitadas: record.cantidadBolsasSolicitadas,
    cantidadBolsasEntregadas: record.cantidadBolsasEntregadas,
    observaciones: record.observaciones,
    nombreRecibe: record.nombreRecibe,
    cedulaRecibe: record.cedulaRecibe,
    firmaRecibeBlob: null,
    nombreEntrega: record.nombreEntrega,
    cedulaEntrega: record.cedulaEntrega,
    firmaEntregaBlob: null,
  };
}

function buildRemisionSaveInput(
  entregaPublicId: string,
  state: EntregaRemisionFormState,
  media: { firmaRecibeMediaId: string; firmaEntregaMediaId: string },
  fechaHora = state.fechaHora.trim(),
): SaveEntregaRemisionInput {
  return {
    entregaPublicId,
    nombreDespacha: state.nombreDespacha.trim(),
    vehiculo: state.vehiculo.trim(),
    fechaHora,
    cantidadSolicitadas: clampEntregaRemisionSolicitadas(state.cantidadSolicitadas),
    cantidadEntregadas: Math.max(0, Math.trunc(state.cantidadEntregadas)),
    cantidadBolsasSolicitadas: clampEntregaRemisionSolicitadas(state.cantidadBolsasSolicitadas),
    cantidadBolsasEntregadas: clampEntregaRemisionSolicitadas(state.cantidadBolsasEntregadas),
    observaciones: state.observaciones.trim(),
    nombreRecibe: state.nombreRecibe.trim(),
    cedulaRecibe: state.cedulaRecibe.trim(),
    firmaRecibeMediaId: media.firmaRecibeMediaId,
    nombreEntrega: state.nombreEntrega.trim(),
    cedulaEntrega: state.cedulaEntrega.trim(),
    firmaEntregaMediaId: media.firmaEntregaMediaId,
  };
}

async function persistRemisionMedia(
  service: EntregaApplicationService,
  state: EntregaRemisionFormState,
  existing?: { firmaRecibeMediaId: string | null; firmaEntregaMediaId: string | null },
): Promise<{ firmaRecibeMediaId: string; firmaEntregaMediaId: string }> {
  const firmaRecibeMediaId = await resolveEntregaMediaId(
    service.media,
    state.firmaRecibeBlob,
    existing?.firmaRecibeMediaId,
    'Firma de quien recibe',
  );
  const firmaEntregaMediaId = await resolveEntregaMediaId(
    service.media,
    state.firmaEntregaBlob,
    existing?.firmaEntregaMediaId,
    'Firma de quien entrega',
  );
  return { firmaRecibeMediaId, firmaEntregaMediaId };
}

async function assertPersistedRemisionMedia(
  service: EntregaApplicationService,
  media: { firmaRecibeMediaId: string; firmaEntregaMediaId: string },
): Promise<void> {
  await assertEntregaMediaStored(service.media, media.firmaRecibeMediaId, 'Firma de quien recibe');
  await assertEntregaMediaStored(service.media, media.firmaEntregaMediaId, 'Firma de quien entrega');
  for (const [id, label] of [
    [media.firmaRecibeMediaId, 'Firma de quien recibe'],
    [media.firmaEntregaMediaId, 'Firma de quien entrega'],
  ] as const) {
    const blob = await service.media.get(id);
    const ink = blob ? await blobHasSignatureInk(blob) : false;
    if (!blob || ink !== true) {
      throw new Error(signatureInkAssertError(ink, label));
    }
  }
}

export type EntregaRemisionFirmaKind = 'recibe' | 'entrega';

/** Persist remisión signature to IndexedDB immediately; patch SQLite when the row already exists. */
export async function autosaveEntregaRemisionFirma(
  service: EntregaApplicationService,
  opts: {
    kind: EntregaRemisionFirmaKind;
    blob: Blob;
    currentMediaId: string | null | undefined;
    remisionPublicId: string | null;
    entregaPublicId: string;
  },
): Promise<{ ok: true; mediaId: string } | { ok: false; message: string }> {
  const label = opts.kind === 'recibe' ? 'Firma de quien recibe' : 'Firma de quien entrega';
  try {
    const ink = await blobHasSignatureInk(opts.blob);
    if (!signatureInkAcceptableForSave(opts.blob, ink)) {
      return { ok: false, message: `${label} está vacía.` };
    }
    const mediaId = await persistVerifiedEntregaBlob(service.media, opts.blob, label, opts.currentMediaId);
    const remisionId = opts.remisionPublicId?.trim();
    if (remisionId) {
      const patched = await service.children.patchRemisionDeliveryMedia(
        remisionId,
        opts.entregaPublicId,
        opts.kind === 'recibe' ? { firmaRecibeMediaId: mediaId } : { firmaEntregaMediaId: mediaId },
      );
      if (!patched) {
        await service.media.delete(mediaId).catch(() => {});
        return { ok: false, message: 'No se pudo registrar la firma de la remisión.' };
      }
    }
    await service.flushLocalChanges();
    return { ok: true, mediaId };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : String(e) };
  }
}

function resolveRemisionCantidades(
  state: EntregaRemisionFormState,
  requiresParticipantes: boolean,
  participanteCount: number,
  bolsasReutilizablesCount: number,
): Pick<EntregaRemisionFormState, 'cantidadEntregadas' | 'cantidadBolsasEntregadas'> {
  if (!requiresParticipantes) {
    return {
      cantidadEntregadas: state.cantidadEntregadas,
      cantidadBolsasEntregadas: state.cantidadBolsasEntregadas,
    };
  }
  return {
    cantidadEntregadas: participanteCount,
    cantidadBolsasEntregadas: bolsasReutilizablesCount,
  };
}

export async function saveEntregaRemisionWithFeedback(
  service: EntregaApplicationService,
  entregaPublicId: string,
  state: EntregaRemisionFormState,
  participanteCount: number,
  requiresParticipantes = true,
  existingMedia: { firmaRecibeMediaId: string | null; firmaEntregaMediaId: string | null } = {
    firmaRecibeMediaId: null,
    firmaEntregaMediaId: null,
  },
  bolsasReutilizablesCount = 0,
): Promise<FormUseCaseResult> {
  const cantidades = resolveRemisionCantidades(
    state,
    requiresParticipantes,
    participanteCount,
    bolsasReutilizablesCount,
  );
  const fieldErrors = validateEntregaRemisionFormErrors(
    { ...state, ...cantidades },
    {
      participanteCount,
      requiresParticipantes,
      existingFirmaRecibeMediaId: existingMedia.firmaRecibeMediaId,
      existingFirmaEntregaMediaId: existingMedia.firmaEntregaMediaId,
    },
  );
  const validation = firstFieldError(fieldErrors);
  if (validation) return { ok: false, message: validation, fieldErrors };
  const existingRemisiones = await service.children.listRemisionesByEntrega(entregaPublicId);
  if (existingRemisiones.length > 0) {
    return { ok: false, message: 'Ya existe una remisión para este punto de entrega.', fieldErrors: {} };
  }
  const fechaHora = await fechaHoraAlignedToEntrega(service, entregaPublicId);
  if (!fechaHora) {
    return { ok: false, message: 'No se encontró la fecha del punto de entrega.', fieldErrors: {} };
  }
  try {
    const media = await persistRemisionMedia(service, state, existingMedia);
    await assertPersistedRemisionMedia(service, media);
    const { publicId } = await service.children.saveRemision(
      buildRemisionSaveInput(
        entregaPublicId,
        { ...state, ...cantidades },
        media,
        fechaHora,
      ),
    );
    await service.flushLocalChanges();
    return { ok: true, message: `Remisión guardada (Ref. ${shortPublicId(publicId)}).` };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : String(e), fieldErrors: {} };
  }
}

export async function updateEntregaRemisionWithFeedback(
  service: EntregaApplicationService,
  publicId: string,
  entregaPublicId: string,
  state: EntregaRemisionFormState,
  existing: { firmaRecibeMediaId: string | null; firmaEntregaMediaId: string | null },
  participanteCount: number,
  requiresParticipantes = true,
  bolsasReutilizablesCount = 0,
): Promise<FormUseCaseResult> {
  const cantidades = resolveRemisionCantidades(
    state,
    requiresParticipantes,
    participanteCount,
    bolsasReutilizablesCount,
  );
  const fieldErrors = validateEntregaRemisionFormErrors(
    { ...state, ...cantidades },
    {
      existingFirmaRecibeMediaId: existing.firmaRecibeMediaId,
      existingFirmaEntregaMediaId: existing.firmaEntregaMediaId,
      participanteCount,
      requiresParticipantes,
    },
  );
  const validation = firstFieldError(fieldErrors);
  if (validation) return { ok: false, message: validation, fieldErrors };
  const fechaHora = await fechaHoraAlignedToEntrega(service, entregaPublicId, state.fechaHora);
  if (!fechaHora) {
    return { ok: false, message: 'No se encontró la fecha del punto de entrega.', fieldErrors: {} };
  }
  try {
    const media = await persistRemisionMedia(service, state, existing);
    await assertPersistedRemisionMedia(service, media);
    const updated = await service.children.updateRemision(
      publicId,
      buildRemisionSaveInput(
        entregaPublicId,
        { ...state, ...cantidades },
        media,
        fechaHora,
      ),
    );
    if (!updated) return { ok: false, message: 'No se encontró la remisión.', fieldErrors: {} };
    await service.flushLocalChanges();
    return { ok: true, message: 'Remisión actualizada.' };
  } catch (e) {
    return { ok: false, message: e instanceof Error ? e.message : String(e), fieldErrors: {} };
  }
}
